import React, { useState, useEffect } from 'react';
import { ClientEntity } from '../types';
import { urlPublicaDoSite } from '../lib/site-publico';
import { SiteEditor } from './SiteEditor';
import { EditorDeTemplate } from './EditorDeTemplate';
import { MeuNegocioDashboard } from './MeuNegocioDashboard';
import { TrafficManagement } from './TrafficManagement';
import { SmartCrmBoard } from './SmartCrmBoard';
import { BrandCustomizer } from './BrandCustomizer';
import { ContaDoCliente } from './ContaDoCliente';
import { 
  LayoutTemplate, Settings, LogOut, Globe, Home, BrainCircuit, 
  Palette, Database, LifeBuoy, ExternalLink, Copy, CheckCircle2, 
  MessageCircle, MapPin, Phone, Clock, Tag, Check, ChevronRight,
  Link as LinkIcon, Loader2, AlertCircle, Target, Share2, Bot, Lock,
  CreditCard, BarChart, Network, FolderOpen, LayoutGrid, Kanban,
  Users, UserCheck, ShieldCheck, Sparkles, Send, Eye, ToggleLeft, ToggleRight,
  Sliders
} from 'lucide-react';
import { db } from '../services/db';
import { toast } from 'sonner';

import { ClientPaymentBoard } from './ClientPaymentBoard';
import { WhatsAppAgentDashboard } from './WhatsAppAgentDashboard';
import { SocialMediaDashboard } from './SocialMediaDashboard';
import { SchedulingDashboard } from './SchedulingDashboard';
import { ReportsDashboard } from './ReportsDashboard';
import { TrackingIntegrations } from './TrackingIntegrations';
import { AssetsDashboard } from './AssetsDashboard';
import { CreativeLibrary } from './CreativeLibrary';
import { DecisionInbox } from './DecisionInbox';
import { CustomerPortal } from './CustomerPortal';
import { ClientDossierManager } from './ClientDossierManager';
import { useStore } from '../store/useStore';
import { extractBrandingFromHtml, ClientBranding, adjustColorBrightness } from '../utils/brandExtractor';

interface ClientDashboardProps {
  client: ClientEntity;
  onLogout: () => void;
  onClientUpdate?: () => void;
}

export const ClientDashboard: React.FC<ClientDashboardProps> = ({ client, onLogout, onClientUpdate }) => {
  const [activeTab, setActiveTab] = useState<
    'inicio' | 'crm' | 'carteira' | 'estrategia' | 'editor' | 'dados' | 'dominio' | 'suporte' | 
    'trafego' | 'social' | 'whatsapp' | 'financeiro' | 'negocio' | 'agendamento' | 
    'relatorios' | 'integracoes' | 'ativos' | 'criativos' | 'atendimento' | 'identidade' | 'conta'
    // Quem entra com a senha padrao cai direto em "Conta". Nao e um empurrao
    // gratuito: enquanto a senha de fabrica valer, ela e a mesma de todo cliente
    // que ainda nao trocou — e quem conhece o padrao entra nesta conta.
  >(client.precisaTrocarSenha ? 'conta' : 'inicio');

  /**
   * A senha ainda e a de fabrica?
   *
   * Fica em estado porque a troca acontece DENTRO desta arvore: se o aviso
   * dependesse so da prop, o painel continuaria cobrando a troca depois de ela
   * ter sido feita, ate a proxima carga da pagina.
   */
  const [precisaTrocarSenha, setPrecisaTrocarSenha] = useState(
    client.precisaTrocarSenha === true,
  );

  const { appointments, leads } = useStore();
  const [financeiroSubTab, setFinanceiroSubTab] = useState<'site' | 'trafego' | 'social' | 'agente' | 'assinaturas'>('site');
  const [isVerifyingPayment, setIsVerifyingPayment] = useState(false);
  const [copiedPortalLink, setCopiedPortalLink] = useState(false);
  const [portalViewMode, setPortalViewMode] = useState<'gestao' | 'preview'>('gestao');

  // Portal feature toggles
  const [enableOnlineBooking, setEnableOnlineBooking] = useState(true);
  const [enablePixPayment, setEnablePixPayment] = useState(true);
  const [enableDocsDownload, setEnableDocsDownload] = useState(true);
  const [enableDirectSupport, setEnableDirectSupport] = useState(true);

  // Dynamic Client Design System & Branding
  const [branding, setBranding] = useState<ClientBranding>(() => {
    try {
      const saved = localStorage.getItem(`client_branding_${client.id}`);
      if (saved) return JSON.parse(saved);
    } catch (e) {
      console.warn('Error reading saved branding', e);
    }
    return extractBrandingFromHtml(client.htmlContent, client.data);
  });

  useEffect(() => {
    const fetchStoredBrain = async () => {
      try {
        const brain = await db.getClientBusinessBrain(client.id);
        if (brain?.branding_custom) {
          setBranding(brain.branding_custom);
        } else {
          const extracted = extractBrandingFromHtml(client.htmlContent, client.data);
          setBranding(extracted);
        }
      } catch (e) {
        console.warn('Could not sync brain branding', e);
      }
    };
    fetchStoredBrain();
  }, [client.id, client.htmlContent, client.data]);

  useEffect(() => {
    const handleCustomNav = (e: any) => {
      if (e.detail && typeof e.detail === 'string') {
        setActiveTab(e.detail as any);
      }
    };
    window.addEventListener('navigate-client-tab', handleCustomNav);
    return () => window.removeEventListener('navigate-client-tab', handleCustomNav);
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const payment = urlParams.get('payment');
    const sessionId = urlParams.get('session_id');

    if (payment === 'success' && sessionId && client.status === 'pending') {
      verifyPayment(sessionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verifyPayment = async (sessionId: string) => {
    setIsVerifyingPayment(true);
    try {
      const response = await fetch('/api/verify-payment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId }),
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Pagamento confirmado com sucesso! Seu site está liberado.');
        window.history.replaceState({}, document.title, window.location.pathname + window.location.hash.split('?')[0]);
        if (onClientUpdate) onClientUpdate();
      } else {
        toast.error('Pagamento ainda não processado. Tente novamente em instantes.');
      }
    } catch (error) {
      console.error(error);
      toast.error('Erro ao verificar o pagamento.');
    } finally {
      setIsVerifyingPayment(false);
    }
  };

  const [isEditing, setIsEditing] = useState(false);
  /** O outro editor: troca o MODELO inteiro, em vez de retocar o HTML publicado. */
  const [editandoModelo, setEditandoModelo] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  // Domain State
  const [customDomain, setCustomDomain] = useState(client.customDomain || '');
  const [domainInput, setDomainInput] = useState('');
  const [isLinkingDomain, setIsLinkingDomain] = useState(false);
  const [domainError, setDomainError] = useState<string | null>(null);
  const [domainStatus, setDomainStatus] = useState<'pending' | 'active' | 'error' | null>(null);

  const [businessBrain, setBusinessBrain] = useState<any>(null);

  useEffect(() => {
    const fetchBrain = async () => {
      const data = await db.getClientBusinessBrain(client.id);
      setBusinessBrain(data);
    };
    fetchBrain();
  }, [client.id]);

  // O endereco publico sai do servidor (`urlPublica`) ou do subdominio padrao.
  // O `#/site/` de antes apontava para o painel e exigia sessao de operador.
  // `portalLink` abaixo continua no painel de proposito: o portal do cliente
  // e uma tela DAQUI, nao o site publicado.
  const siteLink = customDomain ? `https://${customDomain}` : urlPublicaDoSite(client);
  const portalLink = `${window.location.origin}${window.location.pathname}#/atendimento/${client.slug}`;

  const copyPortalLink = () => {
    navigator.clipboard.writeText(portalLink);
    setCopiedPortalLink(true);
    toast.success("Link do Portal do Cliente copiado!");
    setTimeout(() => setCopiedPortalLink(false), 2500);
  };

  const handleSharePortalWhatsApp = () => {
    const message = encodeURIComponent(`Olá! Segue o link de acesso ao seu portal de atendimento da ${client.data.name}: ${portalLink}`);
    window.open(`https://wa.me/?text=${message}`, '_blank');
  };

  useEffect(() => {
    if (customDomain) {
      checkDomainStatus(customDomain);
    }
  }, [customDomain]);

  const checkDomainStatus = async (domain: string) => {
    try {
      const res = await fetch(`/api/domains/${domain}`);
      const data = await res.json();
      if (data.success && data.data) {
        if (data.data.verified) {
          setDomainStatus('active');
        } else {
          setDomainStatus('pending');
        }
      }
    } catch (err) {
      console.error("Error checking domain status", err);
    }
  };

  const handleLinkDomain = async () => {
    if (!domainInput || !domainInput.includes('.')) {
      setDomainError("Por favor, insira um domínio válido (ex: seunegocio.com.br)");
      return;
    }

    setIsLinkingDomain(true);
    setDomainError(null);

    let cleanDomain = domainInput.toLowerCase().trim();
    cleanDomain = cleanDomain.replace(/^https?:\/\//, '');
    cleanDomain = cleanDomain.replace(/^www\./, '');
    cleanDomain = cleanDomain.split('/')[0];

    try {
      const res = await fetch('/api/domains', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain: cleanDomain })
      });

      const data = await res.json();

      if (!res.ok) {
        const errorMessage = typeof data.error === 'object' ? data.error.message : data.error;
        throw new Error(errorMessage || "Erro ao configurar domínio na Vercel");
      }

      await db.updateClientDomain(client.id, cleanDomain);
      setCustomDomain(cleanDomain);
      setDomainStatus('pending');
      setDomainInput('');
      toast.success('Domínio vinculado com sucesso!');
    } catch (err: any) {
      setDomainError(err.message);
    } finally {
      setIsLinkingDomain(false);
    }
  };

  const handleRemoveDomain = async () => {
    if (!customDomain) return;
    
    if (!window.confirm(`Tem certeza que deseja remover o domínio ${customDomain}?`)) {
      return;
    }

    setIsLinkingDomain(true);
    try {
      await fetch(`/api/domains/${customDomain}`, { method: 'DELETE' });
      await db.updateClientDomain(client.id, null);
      setCustomDomain('');
      setDomainStatus(null);
      toast.success('Domínio removido.');
    } catch (err) {
      console.error(err);
      toast.error("Erro ao remover domínio.");
    } finally {
      setIsLinkingDomain(false);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedLink(true);
      toast.success('Link copiado!');
      setTimeout(() => setCopiedLink(false), 2000);
    } catch (err) {
      console.error('Failed to copy', err);
    }
  };

  if (isVerifyingPayment) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex flex-col items-center justify-center">
        <Loader2 className="animate-spin text-black" size={40} />
        <p className="mt-4 text-gray-500 font-medium">Verificando seu pagamento...</p>
      </div>
    );
  }

  if (isEditing) {
    return (
      <div className="fixed inset-0 z-50 bg-white">
        <SiteEditor 
          clientEntity={client} 
          onClose={() => setIsEditing(false)} 
          hideCloseButton={false} 
        />
      </div>
    );
  }

  if (editandoModelo) {
    return (
      <div className="fixed inset-0 z-50 bg-white">
        <EditorDeTemplate
          clienteId={client.id}
          onFechar={() => setEditandoModelo(false)}
        />
      </div>
    );
  }

  // Active theme color shortcuts
  const primaryBg = { backgroundColor: branding.primaryColor };
  const primaryText = { color: branding.primaryColor };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'conta':
        return (
          <ContaDoCliente
            client={client}
            onLogout={onLogout}
            corPrimaria={branding.primaryColor}
            precisaTrocarSenha={precisaTrocarSenha}
            onSenhaTrocada={() => setPrecisaTrocarSenha(false)}
          />
        );

      case 'identidade':
        return (
          <BrandCustomizer 
            client={client} 
            branding={branding} 
            onUpdateBranding={(newB) => setBranding(newB)} 
          />
        );

      case 'crm':
        return <SmartCrmBoard />;

      case 'financeiro':
        return (
          <div className="space-y-6 animate-in fade-in duration-300">
            <h2 className="text-2xl font-bold text-gray-900 tracking-tight">Financeiro & Assinaturas</h2>
            <p className="text-gray-500">Acompanhe seus pagamentos e serviços ativos.</p>

            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="flex overflow-x-auto border-b border-gray-200 hide-scrollbar">
                {(['site', 'trafego', 'social', 'agente', 'assinaturas'] as const).map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setFinanceiroSubTab(tab)}
                    className={`px-6 py-4 text-sm font-medium whitespace-nowrap transition-colors relative ${
                      financeiroSubTab === tab 
                        ? 'font-bold' 
                        : 'text-gray-500 hover:text-gray-800'
                    }`}
                    style={financeiroSubTab === tab ? primaryText : undefined}
                  >
                    {tab === 'site' && 'Site & Setup'}
                    {tab === 'trafego' && 'Gestão de Tráfego'}
                    {tab === 'social' && 'Social Media'}
                    {tab === 'agente' && 'Agente IA'}
                    {tab === 'assinaturas' && 'Assinaturas'}
                    {financeiroSubTab === tab && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5" style={primaryBg} />
                    )}
                  </button>
                ))}
              </div>
              
              <div className="p-6 md:p-8">
                {financeiroSubTab === 'site' ? (
                  client.status === 'paid' ? (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-8 flex flex-col items-center justify-center text-center">
                      <CheckCircle2 size={48} className="text-emerald-500 mb-4" />
                      <h4 className="text-xl font-bold text-emerald-950 mb-2">Projeto Quitado</h4>
                      <p className="text-emerald-800 max-w-sm text-sm">
                        O desenvolvimento e setup do seu ecossistema digital está 100% quitado e liberado.
                      </p>
                    </div>
                  ) : (
                    <div className="-mx-6 md:-mx-8 -my-6 md:-my-8">
                      <ClientPaymentBoard client={client} onLogout={onLogout} embedded />
                    </div>
                  )
                ) : (
                  <div className="flex flex-col items-center justify-center text-center py-12 px-4">
                    <div className="w-16 h-16 bg-gray-50 text-gray-400 rounded-2xl flex items-center justify-center mb-4 border border-gray-200">
                      <Lock size={24} />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {financeiroSubTab === 'trafego' && 'Tráfego Pago (Meta & Google Ads)'}
                      {financeiroSubTab === 'social' && 'Gestão Estratégica de Redes Sociais'}
                      {financeiroSubTab === 'agente' && 'Agente de Vendas WhatsApp IA'}
                      {financeiroSubTab === 'assinaturas' && 'Planos e Assinaturas Recorrentes'}
                    </h3>
                    <p className="text-gray-500 text-sm max-w-md mb-6">
                      Este serviço pode ser contratado ou gerenciado diretamente com nosso time para acelerar seus resultados.
                    </p>
                    <a 
                      href="https://wa.me/5511999999999" 
                      target="_blank" 
                      rel="noreferrer"
                      className="inline-flex items-center justify-center gap-2 text-white px-5 py-2.5 rounded-xl font-bold transition-opacity hover:opacity-90 text-sm shadow-sm"
                      style={primaryBg}
                    >
                      <MessageCircle size={18} />
                      Falar com Consultor
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 'atendimento':
        return (
          <div className="space-y-6 animate-in fade-in duration-300">
            {/* Top Management Header Card */}
            <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-xs bg-indigo-50 text-indigo-700 px-3 py-1 rounded-full font-bold border border-indigo-200">
                    Central de Atendimento ao Consumidor
                  </span>
                  <span className="text-xs bg-emerald-50 text-emerald-700 px-2.5 py-0.5 rounded-full font-bold">
                    Online & Ativo
                  </span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mt-2">Portal do Cliente Final ({client.data.name})</h3>
                <p className="text-xs text-gray-500 mt-1 max-w-2xl">
                  Página personalizada onde seus pacientes e clientes acessam para consultar agendamentos, emitir faturas Pix, enviar comprovantes e falar com o suporte.
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
                <button
                  onClick={copyPortalLink}
                  className="flex-1 md:flex-none bg-gray-100 hover:bg-gray-200 text-gray-800 text-xs font-bold px-4 py-2.5 rounded-xl transition-colors flex items-center justify-center gap-1.5 shadow-sm"
                >
                  {copiedPortalLink ? <CheckCircle2 size={15} className="text-green-600" /> : <Copy size={15} />}
                  <span>{copiedPortalLink ? 'Link Copiado!' : 'Copiar Link'}</span>
                </button>
                <button
                  onClick={handleSharePortalWhatsApp}
                  className="flex-1 md:flex-none bg-[#25D366] hover:bg-[#128C7E] text-white text-xs font-bold px-4 py-2.5 rounded-xl transition-colors flex items-center justify-center gap-1.5 shadow-sm"
                >
                  <Send size={15} />
                  <span>Enviar no WhatsApp</span>
                </button>
                <a
                  href={portalLink}
                  target="_blank"
                  rel="noreferrer"
                  className="flex-1 md:flex-none text-white text-xs font-bold px-4 py-2.5 rounded-xl transition-opacity hover:opacity-90 flex items-center justify-center gap-1.5 shadow-sm"
                  style={primaryBg}
                >
                  <ExternalLink size={15} /> Abrir em Nova Aba
                </a>
              </div>
            </div>

            {/* View Mode Toggle */}
            <div className="flex items-center justify-between bg-white p-2.5 rounded-xl border border-gray-200 shadow-sm">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPortalViewMode('gestao')}
                  className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${
                    portalViewMode === 'gestao' ? 'text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'
                  }`}
                  style={portalViewMode === 'gestao' ? primaryBg : undefined}
                >
                  Cockpit de Gestão
                </button>
                <button
                  onClick={() => setPortalViewMode('preview')}
                  className={`px-4 py-2 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
                    portalViewMode === 'preview' ? 'text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'
                  }`}
                  style={portalViewMode === 'preview' ? primaryBg : undefined}
                >
                  <Eye size={14} /> Pré-visualizar Portal do Cliente
                </button>
              </div>

              <span className="text-[11px] text-gray-400 font-mono hidden sm:inline">
                {portalLink}
              </span>
            </div>

            {portalViewMode === 'gestao' ? (
              <div className="space-y-6 animate-in fade-in duration-300">
                {/* Metrics */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm">
                    <div className="text-2xl font-bold text-gray-900">{appointments?.length || 0}</div>
                    <p className="text-xs font-bold text-gray-700 mt-1">Agendamentos Solicitados</p>
                    <p className="text-[11px] text-gray-400 mt-0.5">Sincronizados com seu calendário</p>
                  </div>
                  <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm">
                    <div className="text-2xl font-bold text-emerald-600">100%</div>
                    <p className="text-xs font-bold text-gray-700 mt-1">Pix Instantâneo Liberado</p>
                    <p className="text-[11px] text-gray-400 mt-0.5">Recebimento direto via chave Pix</p>
                  </div>
                  <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm">
                    <div className="text-2xl font-bold" style={primaryText}>Ativo</div>
                    <p className="text-xs font-bold text-gray-700 mt-1">Suporte Humanizado</p>
                    <p className="text-[11px] text-gray-400 mt-0.5">Direcionamento para WhatsApp</p>
                  </div>
                </div>

                {/* Portal Features Settings */}
                <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm">
                  <h4 className="font-bold text-gray-900 text-sm mb-4">Módulos Habilitados no Portal do Cliente</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl flex items-center justify-between">
                      <div>
                        <h5 className="font-bold text-gray-900 text-xs">Agendamento Online de Serviços</h5>
                        <p className="text-[11px] text-gray-500 mt-0.5">Permite que o cliente escolha dia e horário</p>
                      </div>
                      <button
                        onClick={() => { setEnableOnlineBooking(!enableOnlineBooking); toast.success('Configuração atualizada!'); }}
                        className={`text-xs font-bold px-3 py-1.5 rounded-lg transition-colors ${
                          enableOnlineBooking ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-200 text-gray-600'
                        }`}
                      >
                        {enableOnlineBooking ? 'Ativo' : 'Pausado'}
                      </button>
                    </div>

                    <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl flex items-center justify-between">
                      <div>
                        <h5 className="font-bold text-gray-900 text-xs">Pagamento via Chave Pix</h5>
                        <p className="text-[11px] text-gray-500 mt-0.5">Exibe chave e QR Code para faturas</p>
                      </div>
                      <button
                        onClick={() => { setEnablePixPayment(!enablePixPayment); toast.success('Configuração atualizada!'); }}
                        className={`text-xs font-bold px-3 py-1.5 rounded-lg transition-colors ${
                          enablePixPayment ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-200 text-gray-600'
                        }`}
                      >
                        {enablePixPayment ? 'Ativo' : 'Pausado'}
                      </button>
                    </div>

                    <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl flex items-center justify-between">
                      <div>
                        <h5 className="font-bold text-gray-900 text-xs">Download de Documentos & Laudos</h5>
                        <p className="text-[11px] text-gray-500 mt-0.5">Disponibiliza recibos e arquivos para o cliente</p>
                      </div>
                      <button
                        onClick={() => { setEnableDocsDownload(!enableDocsDownload); toast.success('Configuração atualizada!'); }}
                        className={`text-xs font-bold px-3 py-1.5 rounded-lg transition-colors ${
                          enableDocsDownload ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-200 text-gray-600'
                        }`}
                      >
                        {enableDocsDownload ? 'Ativo' : 'Pausado'}
                      </button>
                    </div>

                    <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl flex items-center justify-between">
                      <div>
                        <h5 className="font-bold text-gray-900 text-xs">Botão de Suporte WhatsApp</h5>
                        <p className="text-[11px] text-gray-500 mt-0.5">Link direto para o seu canal de atendimento</p>
                      </div>
                      <button
                        onClick={() => { setEnableDirectSupport(!enableDirectSupport); toast.success('Configuração atualizada!'); }}
                        className={`text-xs font-bold px-3 py-1.5 rounded-lg transition-colors ${
                          enableDirectSupport ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-200 text-gray-600'
                        }`}
                      >
                        {enableDirectSupport ? 'Ativo' : 'Pausado'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="rounded-2xl border border-gray-200 shadow-sm overflow-hidden bg-white animate-in fade-in duration-300">
                <div className="p-3 bg-gray-100 border-b border-gray-200 text-xs text-gray-600 flex items-center justify-between">
                  <span>Visualização simulada do cliente final</span>
                  <button 
                    onClick={() => setPortalViewMode('gestao')}
                    className="text-xs font-bold hover:underline"
                    style={primaryText}
                  >
                    Voltar ao Cockpit de Gestão
                  </button>
                </div>
                <CustomerPortal slug={client.slug} clientEntity={client} />
              </div>
            )}
          </div>
        );

      case 'carteira':
        return <ClientDossierManager client={client} primaryColor={branding.primaryColor} />;
      case 'trafego':
        return <TrafficManagement client={client} />;
      case 'social':
        return <SocialMediaDashboard client={client} />;
      case 'whatsapp':
        return <WhatsAppAgentDashboard client={client} />;
      case 'agendamento':
        return <SchedulingDashboard client={client} />;
      case 'relatorios':
        return <ReportsDashboard client={client} />;
      case 'integracoes':
        return <TrackingIntegrations client={client} />;
      case 'ativos':
        return <AssetsDashboard client={client} />;
      case 'criativos':
        return <CreativeLibrary client={client} />;
      case 'inicio':
        return (
          <div className="space-y-6 animate-in fade-in duration-300">
            <div className="bg-white p-8 rounded-2xl border border-gray-200 shadow-sm">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-bold px-2.5 py-0.5 rounded-full" style={{ backgroundColor: adjustColorBrightness(branding.primaryColor, 85), color: branding.primaryColor }}>
                      White-Label Ativo
                    </span>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 tracking-tight">
                    Olá, {client.data.name}! 👋
                  </h2>
                  <p className="text-gray-500 text-xs mt-0.5">Painel de controle integrado e personalizado para sua empresa.</p>
                </div>

                <button
                  onClick={() => setActiveTab('identidade')}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-bold border border-gray-200 hover:bg-gray-50 transition-colors text-gray-700 shrink-0"
                >
                  <Palette size={14} style={primaryText} />
                  Personalizar Marca & Cores
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-gray-50 p-6 rounded-xl border border-gray-200 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-3 h-3 rounded-full bg-emerald-500 animate-pulse"></div>
                      <h3 className="font-bold text-gray-900">Site Oficial</h3>
                    </div>
                    <p className="text-xs text-gray-600 mb-4">Seu site de alta conversão está online.</p>
                  </div>
                  <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg p-2">
                    <span className="text-xs font-mono text-gray-500 truncate flex-1 pl-2">{siteLink}</span>
                    <button onClick={() => copyToClipboard(siteLink)} className="p-2 text-gray-400 hover:text-black hover:bg-gray-50 rounded-md transition-colors" title="Copiar Link">
                      {copiedLink ? <CheckCircle2 size={16} className="text-green-600" /> : <Copy size={16} />}
                    </button>
                    <a href={siteLink} target="_blank" rel="noreferrer" className="p-2 text-gray-400 hover:text-black hover:bg-gray-50 rounded-md transition-colors" title="Abrir Site">
                      <ExternalLink size={16} />
                    </a>
                  </div>
                </div>

                <div className="bg-gray-50 p-6 rounded-xl border border-gray-200 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-3 h-3 rounded-full" style={primaryBg}></div>
                      <h3 className="font-bold text-gray-900">Portal do Cliente</h3>
                    </div>
                    <p className="text-xs text-gray-600 mb-4">Acompanhamento, Pix e agendamento para clientes.</p>
                  </div>
                  <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg p-2">
                    <span className="text-xs font-mono text-gray-500 truncate flex-1 pl-2">{portalLink}</span>
                    <button onClick={copyPortalLink} className="p-2 text-gray-400 hover:text-black hover:bg-gray-50 rounded-md transition-colors" title="Copiar Link do Portal">
                      {copiedPortalLink ? <CheckCircle2 size={16} className="text-green-600" /> : <Copy size={16} />}
                    </button>
                    <button onClick={() => setActiveTab('atendimento')} className="p-2 text-gray-400 hover:text-black hover:bg-gray-50 rounded-md transition-colors" title="Ver Portal">
                      <ExternalLink size={16} />
                    </button>
                  </div>
                </div>

                <div className="p-6 rounded-xl text-white flex flex-col justify-between shadow-md" style={{ background: `linear-gradient(135deg, ${branding.primaryColor}, ${branding.secondaryColor || '#000'})` }}>
                  <div>
                    <h3 className="font-bold mb-2 flex items-center gap-2">
                      <Palette size={18} className="text-white/80" />
                      Editor Visual
                    </h3>
                    <p className="text-xs text-white/80 mb-6">Atualize textos, cores e imagens do seu site em tempo real.</p>
                  </div>
                  <button 
                    onClick={() => setActiveTab('editor')}
                    className="w-full bg-white text-black hover:bg-gray-100 px-4 py-2 rounded-lg text-xs font-bold transition-colors flex items-center justify-center gap-2"
                  >
                    Acessar Editor <ChevronRight size={14} />
                  </button>
                </div>
              </div>
            </div>

            {/* Checklist Widget */}
            <div className="bg-white p-8 rounded-2xl border border-gray-200 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                 <div className="w-10 h-10 flex items-center justify-center rounded-xl text-white" style={primaryBg}>
                   <Target size={20} />
                 </div>
                 <div>
                   <h3 className="text-xl font-bold text-gray-900">Checklist de Fundações</h3>
                   <p className="text-sm text-gray-500">Próximos passos para a inteligência artificial assumir o controle</p>
                 </div>
              </div>
              
              <div className="space-y-4">
                 <div className="flex items-start gap-4 p-4 rounded-xl border border-gray-100 bg-gray-50">
                    <div className="mt-0.5">
                       {businessBrain?.objectives_data?.ticketMedio && businessBrain?.objectives_data?.metaOtimizacao ? (
                         <CheckCircle2 size={24} className="text-green-500" />
                       ) : (
                         <div className="w-6 h-6 rounded-full border-2 border-gray-300" />
                       )}
                    </div>
                    <div className="flex-1">
                       <h4 className="font-bold text-sm text-gray-900">Objetivo do Sistema e Economia</h4>
                       <p className="text-xs text-gray-500 mt-1">Defina Ticket Médio, Margem, CAC Máximo e a Meta Principal para a IA trabalhar.</p>
                       {(!businessBrain?.objectives_data?.ticketMedio || !businessBrain?.objectives_data?.metaOtimizacao) && (
                         <button onClick={() => setActiveTab('negocio')} className="mt-3 text-xs font-bold text-white px-4 py-2 rounded-lg hover:opacity-90 transition-opacity" style={primaryBg}>
                           Configurar Economia
                         </button>
                       )}
                    </div>
                 </div>

                 <div className="flex items-start gap-4 p-4 rounded-xl border border-gray-100 bg-gray-50">
                    <div className="mt-0.5">
                       {businessBrain?.connections?.metaConnected || businessBrain?.connections?.googleConnected ? (
                         <CheckCircle2 size={24} className="text-green-500" />
                       ) : (
                         <div className="w-6 h-6 rounded-full border-2 border-gray-300" />
                       )}
                    </div>
                    <div className="flex-1">
                       <h4 className="font-bold text-sm text-gray-900">Central de Integrações e Tracking</h4>
                       <p className="text-xs text-gray-500 mt-1">Conecte Meta Ads, Google Ads ou WhatsApp para liberar os fluxos de dados.</p>
                       {(!businessBrain?.connections?.metaConnected && !businessBrain?.connections?.googleConnected) && (
                         <button onClick={() => setActiveTab('integracoes')} className="mt-3 text-xs font-bold text-white px-4 py-2 rounded-lg hover:opacity-90 transition-opacity" style={primaryBg}>
                           Conectar Integrações
                         </button>
                       )}
                    </div>
                 </div>
                 
                 <div className="flex items-start gap-4 p-4 rounded-xl border border-gray-100 bg-gray-50">
                    <div className="mt-0.5">
                       {(businessBrain?.branding_data?.brandbook?.length > 0 || businessBrain?.media_library?.bRolls?.length > 0) ? (
                         <CheckCircle2 size={24} className="text-green-500" />
                       ) : (
                         <div className="w-6 h-6 rounded-full border-2 border-gray-300" />
                       )}
                    </div>
                    <div className="flex-1">
                       <h4 className="font-bold text-sm text-gray-900">Matéria-Prima & Identidade da Marca</h4>
                       <p className="text-xs text-gray-500 mt-1">Personalize paleta de cores e faça o upload de fotos/vídeos para seu acervo.</p>
                       {(!businessBrain?.branding_data?.brandbook?.length && !businessBrain?.media_library?.bRolls?.length) && (
                         <button onClick={() => setActiveTab('identidade')} className="mt-3 text-xs font-bold text-white px-4 py-2 rounded-lg hover:opacity-90 transition-opacity" style={primaryBg}>
                           Ajustar Identidade
                         </button>
                       )}
                    </div>
                 </div>
              </div>
            </div>

            <DecisionInbox />
          </div>
        );

      case 'estrategia':
        return (
          <div className="w-full flex flex-col bg-white min-h-full animate-in fade-in duration-200">
            {/* Top Header */}
            <div className="border-b border-slate-200 px-8 py-6 bg-white">
              <div className="flex items-center gap-2">
                <span className="px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider bg-slate-100 text-slate-700">
                  Planejamento & Inteligência
                </span>
              </div>
              <h1 className="text-2xl font-black text-slate-900 mt-1 tracking-tight">Estratégia & Blueprint</h1>
              <p className="text-slate-500 text-xs mt-0.5">A inteligência de posicionamento e arquitetura de conversão do seu site.</p>
            </div>

            {/* Strategy Content */}
            <div className="p-8 bg-slate-50/40 space-y-6 flex-1">
              {/* Executive Summary */}
              <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-xs space-y-2">
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-900 flex items-center gap-2">
                  <Target size={15} style={primaryText} /> Resumo Executivo & Posicionamento
                </h3>
                <p className="text-slate-700 leading-relaxed text-xs font-medium">{client.data.summary}</p>
              </div>

              {/* Site Blueprint Modules */}
              <div className="space-y-3">
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-900 flex items-center gap-2">
                  <LayoutTemplate size={15} style={primaryText} /> Blueprint da Estrutura Digital
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {client.data.blueprint.map((item, idx) => (
                    <div key={idx} className="bg-white p-5 rounded-xl border border-slate-200 shadow-xs space-y-3">
                      <div>
                        <h4 className="font-bold text-slate-900 text-sm">{item.title}</h4>
                        <p className="text-slate-500 text-xs mt-1 leading-normal">{item.description}</p>
                      </div>
                      
                      <div className="bg-slate-50 p-3 rounded-lg border border-slate-100">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-0.5">Estratégia UX</span>
                        <p className="text-slate-700 text-xs font-medium">{item.uxStrategy}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Photo Analysis */}
              {client.data.photoAnalysis && (
                <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-xs space-y-2">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-900 flex items-center gap-2">
                    <Palette size={15} style={primaryText} /> Análise Fotográfica & Direção Visual
                  </h3>
                  <p className="text-slate-700 leading-relaxed text-xs font-medium">{client.data.photoAnalysis}</p>
                </div>
              )}
            </div>
          </div>
        );

      case 'negocio':
        return <MeuNegocioDashboard client={client} />;

      case 'editor':
        return (
          <div className="h-full flex flex-col items-center justify-center animate-in fade-in duration-300 max-w-2xl mx-auto text-center py-12">
            <div className="w-20 h-20 bg-gray-100 rounded-2xl flex items-center justify-center mb-6 border border-gray-200 shadow-sm" style={{ color: branding.primaryColor }}>
              <Palette size={32} />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4 tracking-tight">Editor Visual</h2>
            <p className="text-gray-500 mb-8 text-base leading-relaxed">
              Faça alterações no seu site de forma simples e intuitiva. Clique nos textos para editar, altere cores e substitua imagens em tempo real.
            </p>
            <button 
              onClick={() => setIsEditing(true)}
              className="text-white px-8 py-4 rounded-xl font-bold transition-opacity hover:opacity-90 shadow-md flex items-center gap-3 text-base"
              style={primaryBg}
            >
              <LayoutTemplate size={20} />
              Abrir Editor Visual
            </button>
            <p className="text-xs text-gray-400 mt-6">
              As alterações são salvas automaticamente no banco de dados.
            </p>

            {/*
              O segundo caminho, que faltava no painel.

              O editor visual retoca o HTML que já está no ar — bom para ajuste
              fino, inútil para "esse modelo não combinou". Trocar o modelo é
              escolher entre os cinco, repreencher os campos e republicar, e até
              aqui isso só existia na fábrica.
            */}
            <div className="mt-10 pt-8 border-t border-gray-200 w-full">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Trocar o modelo</h3>
              <p className="text-gray-500 text-sm mb-5 leading-relaxed">
                Cinco modelos prontos, cada um com cabeçalho, seções e rodapé próprios.
                Você preenche um formulário já com os seus dados, escolhe as imagens da
                biblioteca e vê a página mudando ao lado.
              </p>
              <button
                onClick={() => setEditandoModelo(true)}
                className="px-6 py-3 rounded-xl font-bold border border-gray-300 text-gray-900 hover:bg-gray-50 transition-colors flex items-center gap-3 mx-auto"
              >
                <LayoutTemplate size={18} />
                Escolher outro modelo
              </button>
            </div>
          </div>
        );

      case 'dados':
        return (
          <div className="space-y-6 animate-in fade-in duration-300">
            <h2 className="text-2xl font-bold text-gray-900 tracking-tight">Dados do Negócio</h2>
            <p className="text-gray-500">Informações utilizadas para personalizar o seu site e canais de atendimento.</p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-6">
                <div>
                  <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Globe size={16} className="text-gray-400" /> Informações Básicas
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-[10px] uppercase tracking-wider font-bold text-gray-400 mb-1">Nome</label>
                      <div className="text-gray-900 text-sm font-semibold">{client.data.name}</div>
                    </div>
                    <div>
                      <label className="block text-[10px] uppercase tracking-wider font-bold text-gray-400 mb-1">Endereço</label>
                      <div className="text-gray-900 text-sm flex items-center gap-2">
                        <MapPin size={14} className="text-gray-400" /> {client.data.address}
                      </div>
                    </div>
                    <div>
                      <label className="block text-[10px] uppercase tracking-wider font-bold text-gray-400 mb-1">Telefone</label>
                      <div className="text-gray-900 text-sm flex items-center gap-2">
                        <Phone size={14} className="text-gray-400" /> {client.data.phoneNumber || 'Não informado'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-6">
                <div>
                  <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Clock size={16} className="text-gray-400" /> Horários de Funcionamento
                  </h3>
                  <div className="text-sm text-gray-600 bg-gray-50 p-4 rounded-xl border border-gray-200">
                    {client.data.openingHours ? String(client.data.openingHours) : 'Segunda a Sexta: 08:00 às 18:00'}
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Tag size={16} className="text-gray-400" /> Categorias & Especialidades
                  </h3>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {(client.data.categories || []).map((cat, i) => (
                      <span key={i} className="px-2.5 py-1 bg-gray-100 text-gray-700 rounded-md text-xs font-semibold">
                        {cat}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'dominio':
        return (
          <div className="space-y-6 animate-in fade-in duration-300">
            <h2 className="text-2xl font-bold text-gray-900 tracking-tight">Domínio Próprio</h2>
            <p className="text-gray-500">Conecte o seu domínio profissional (ex: seunegocio.com.br) ao seu site.</p>

            <div className="bg-white p-8 rounded-2xl border border-gray-200 shadow-sm">
              {!customDomain ? (
                <div className="max-w-xl">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center text-blue-600">
                      <Globe size={20} />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900">Vincular Domínio</h3>
                      <p className="text-sm text-gray-500">Digite o domínio que você registrou.</p>
                    </div>
                  </div>

                  <div className="flex gap-3 mb-4">
                    <input 
                      type="text"
                      value={domainInput}
                      onChange={(e) => setDomainInput(e.target.value)}
                      placeholder="ex: seunegocio.com.br"
                      className="flex-1 border border-gray-300 focus:border-black focus:ring-1 focus:ring-black rounded-xl px-4 py-2.5 text-sm outline-none transition-all"
                    />
                    <button 
                      onClick={handleLinkDomain}
                      disabled={isLinkingDomain || !domainInput}
                      className="text-white px-6 py-2.5 rounded-xl text-sm font-bold transition-opacity hover:opacity-90 disabled:opacity-40 flex items-center gap-2 shadow-sm"
                      style={primaryBg}
                    >
                      {isLinkingDomain ? <Loader2 size={16} className="animate-spin" /> : <LinkIcon size={16} />}
                      Conectar
                    </button>
                  </div>
                  
                  {domainError && (
                    <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg border border-red-100">
                      <AlertCircle size={16} />
                      {domainError}
                    </div>
                  )}

                  <div className="mt-8 bg-gray-50 p-5 rounded-xl border border-gray-200">
                    <h4 className="text-sm font-bold text-gray-900 mb-2">Como funciona?</h4>
                    <ol className="text-xs text-gray-600 space-y-2 list-decimal list-inside">
                      <li>Digite seu domínio acima e clique em Conectar.</li>
                      <li>O sistema vai gerar as instruções de apontamento DNS.</li>
                      <li>Vá no seu provedor (ex: Registro.br) e adicione o apontamento tipo A para 76.76.21.21.</li>
                      <li>Aguarde a propagação (geralmente concluída em poucas horas).</li>
                    </ol>
                  </div>
                </div>
              ) : (
                <div className="max-w-2xl">
                  <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${domainStatus === 'active' ? 'bg-green-50 text-green-600 border border-green-200' : 'bg-yellow-50 text-yellow-600 border border-yellow-200'}`}>
                        <Globe size={24} />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">{customDomain}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          <div className={`w-2 h-2 rounded-full ${domainStatus === 'active' ? 'bg-green-500' : 'bg-yellow-500 animate-pulse'}`}></div>
                          <span className="text-sm font-medium text-gray-600">
                            {domainStatus === 'active' ? 'Domínio Ativo e Seguro (SSL)' : 'Aguardando Propagação DNS'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <button 
                      onClick={handleRemoveDomain}
                      disabled={isLinkingDomain}
                      className="text-red-600 hover:bg-red-50 px-4 py-2 rounded-lg text-sm font-bold transition-colors"
                    >
                      Remover
                    </button>
                  </div>

                  {domainStatus === 'active' && (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 flex items-start gap-4">
                      <CheckCircle2 size={24} className="text-emerald-600 shrink-0" />
                      <div>
                        <h4 className="font-bold text-emerald-950 mb-1">Tudo configurado!</h4>
                        <p className="text-emerald-800 text-xs">Seu site já está ativo e seguro no seu domínio próprio.</p>
                        <a href={`https://${customDomain}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 mt-3 bg-white text-emerald-700 border border-emerald-200 hover:bg-emerald-50 px-3.5 py-1.5 rounded-lg text-xs font-bold transition-colors">
                          Acessar Site <ExternalLink size={13} />
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        );

      case 'suporte':
        return (
          <div className="space-y-6 animate-in fade-in duration-300 max-w-2xl">
            <h2 className="text-2xl font-bold text-gray-900 tracking-tight">Suporte Direto</h2>
            <p className="text-gray-500">Precisa de suporte técnico ou novas customizações? Fale diretamente conosco.</p>

            <div className="bg-white p-8 rounded-2xl border border-gray-200 shadow-sm text-center">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-md font-bold text-2xl text-white" style={primaryBg}>
                {branding.brandInitial}
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Central de Atendimento & Engenharia</h3>
              <p className="text-gray-500 text-xs mb-8 max-w-md mx-auto leading-relaxed">
                Nossa equipe de engenharia e suporte técnico está à disposição para acelerar e proteger a infraestrutura do seu negócio.
              </p>
              
              <a 
                href="https://wa.me/5511999999999" 
                target="_blank" 
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#128C7E] text-white px-6 py-3 rounded-xl font-bold transition-colors shadow-sm text-sm"
              >
                <MessageCircle size={18} />
                Chamar no WhatsApp
              </a>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col md:flex-row font-sans pb-20 md:pb-0">
      
      {/* Mobile Top Header */}
      <div className="md:hidden sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-gray-200 px-4 py-3 flex items-center justify-between shadow-sm">
         <div className="flex items-center gap-3">
           <div className="w-8 h-8 rounded-lg flex items-center justify-center shadow-sm overflow-hidden text-white font-bold" style={primaryBg}>
             {branding.logoUrl ? (
               <img src={branding.logoUrl} alt={client.data.name} className="w-full h-full object-contain p-1" />
             ) : (
               <span className="text-sm leading-none">{branding.brandInitial}</span>
             )}
           </div>
           <div>
             <h2 className="font-bold text-gray-900 leading-tight text-xs truncate max-w-[180px]">{client.data.name}</h2>
             <p className="text-[10px] text-gray-500 font-semibold">Cockpit do Negócio</p>
           </div>
         </div>
         <button onClick={onLogout} className="p-2 text-gray-400 hover:text-red-600 transition-colors bg-gray-50 rounded-full" title="Sair">
           <LogOut size={16} />
         </button>
      </div>

      {/* Sidebar - Desktop */}
      <div className="hidden md:flex w-64 bg-white border-r border-gray-200 flex-col shrink-0 h-screen sticky top-0">
        <div className="p-5 border-b border-gray-100 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-sm shrink-0 overflow-hidden text-white font-bold" style={primaryBg}>
            {branding.logoUrl ? (
              <img src={branding.logoUrl} alt={client.data.name} className="w-full h-full object-contain p-1" />
            ) : (
              <span className="text-lg leading-none">{branding.brandInitial}</span>
            )}
          </div>
          <div className="truncate">
            <h2 className="font-bold text-gray-900 leading-tight text-sm truncate" title={client.data.name}>{client.data.name}</h2>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className="w-2 h-2 rounded-full" style={primaryBg}></span>
              <p className="text-[11px] text-gray-500 font-medium">Sistema Proprietário</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-3 space-y-1 overflow-y-auto hide-scrollbar">
          
          <div className="pb-1.5 pt-1">
            <p className="px-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Principal</p>
          </div>

          <button 
            onClick={() => setActiveTab('inicio')}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-semibold transition-all ${
              activeTab === 'inicio' ? 'text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            }`}
            style={activeTab === 'inicio' ? primaryBg : undefined}
          >
            <Home size={16} />
            Visão Geral
          </button>

          {/* SMART CRM KANBAN HIGHLIGHT */}
          <button 
            onClick={() => setActiveTab('crm')}
            className={`w-full flex items-center justify-between px-3 py-2 rounded-xl text-xs font-semibold transition-all ${
              activeTab === 'crm' ? 'text-white shadow-sm' : 'text-gray-700 hover:bg-gray-100 hover:text-black font-bold'
            }`}
            style={activeTab === 'crm' ? primaryBg : undefined}
          >
            <div className="flex items-center gap-3">
              <Kanban size={16} className={activeTab === 'crm' ? 'text-white' : undefined} style={activeTab !== 'crm' ? primaryText : undefined} />
              <span>Smart CRM & Leads</span>
            </div>
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${activeTab === 'crm' ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-800'}`}>
              {leads?.length || 0}
            </span>
          </button>

          {/* CARTEIRA & DOSSIÊS DE CLIENTES */}
          <button 
            onClick={() => setActiveTab('carteira')}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-semibold transition-all ${
              activeTab === 'carteira' ? 'text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            }`}
            style={activeTab === 'carteira' ? primaryBg : undefined}
          >
            <Users size={16} />
            Carteira & Dossiês
          </button>

          <button 
            onClick={() => setActiveTab('negocio')}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-semibold transition-all ${
              activeTab === 'negocio' ? 'text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            }`}
            style={activeTab === 'negocio' ? primaryBg : undefined}
          >
            <BrainCircuit size={16} />
            Meu Negócio
          </button>

          <button 
            onClick={() => setActiveTab('identidade')}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-semibold transition-all ${
              activeTab === 'identidade' ? 'text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            }`}
            style={activeTab === 'identidade' ? primaryBg : undefined}
          >
            <Palette size={16} />
            Design & Marca
          </button>

          <button 
            onClick={() => setActiveTab('editor')}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-semibold transition-all ${
              activeTab === 'editor' ? 'text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            }`}
            style={activeTab === 'editor' ? primaryBg : undefined}
          >
            <LayoutTemplate size={16} />
            Editor do Site
          </button>

          <button 
            onClick={() => setActiveTab('ativos')}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-semibold transition-all ${
              activeTab === 'ativos' ? 'text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            }`}
            style={activeTab === 'ativos' ? primaryBg : undefined}
          >
            <FolderOpen size={16} />
            Ativos & Materiais
          </button>

          <button 
            onClick={() => setActiveTab('criativos')}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-semibold transition-all ${
              activeTab === 'criativos' ? 'text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            }`}
            style={activeTab === 'criativos' ? primaryBg : undefined}
          >
            <LayoutGrid size={16} />
            Biblioteca Criativos
          </button>

          <button 
            onClick={() => setActiveTab('estrategia')}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-semibold transition-all ${
              activeTab === 'estrategia' ? 'text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            }`}
            style={activeTab === 'estrategia' ? primaryBg : undefined}
          >
            <Target size={16} />
            Estratégia & Blueprint
          </button>

          <button 
            onClick={() => setActiveTab('integracoes')}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-semibold transition-all ${
              activeTab === 'integracoes' ? 'text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            }`}
            style={activeTab === 'integracoes' ? primaryBg : undefined}
          >
            <Network size={16} />
            Integrações & Tracking
          </button>

          <div className="pt-4 pb-1">
            <p className="px-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Comercial & Vendas</p>
          </div>

          <button 
            onClick={() => setActiveTab('trafego')}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-semibold transition-all ${
              activeTab === 'trafego' ? 'text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            }`}
            style={activeTab === 'trafego' ? primaryBg : undefined}
          >
            <Target size={16} />
            Gestão de Tráfego
          </button>

          <button 
            onClick={() => setActiveTab('whatsapp')}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-semibold transition-all ${
              activeTab === 'whatsapp' ? 'text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            }`}
            style={activeTab === 'whatsapp' ? primaryBg : undefined}
          >
            <Bot size={16} />
            Agente WhatsApp IA
          </button>

          <button 
            onClick={() => setActiveTab('agendamento')}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-semibold transition-all ${
              activeTab === 'agendamento' ? 'text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            }`}
            style={activeTab === 'agendamento' ? primaryBg : undefined}
          >
            <Clock size={16} />
            Agendamento & Calendário
          </button>

          <button 
            onClick={() => setActiveTab('social')}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-semibold transition-all ${
              activeTab === 'social' ? 'text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            }`}
            style={activeTab === 'social' ? primaryBg : undefined}
          >
            <Share2 size={16} />
            Redes Sociais
          </button>

          <button 
            onClick={() => setActiveTab('atendimento')}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-semibold transition-all ${
              activeTab === 'atendimento' ? 'text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            }`}
            style={activeTab === 'atendimento' ? primaryBg : undefined}
          >
            <UserCheck size={16} />
            Portal de Atendimento
          </button>

          <button 
            onClick={() => setActiveTab('relatorios')}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-semibold transition-all ${
              activeTab === 'relatorios' ? 'text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            }`}
            style={activeTab === 'relatorios' ? primaryBg : undefined}
          >
            <BarChart size={16} />
            Relatórios Executivos
          </button>

          <div className="pt-4 pb-1">
            <p className="px-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Configurações</p>
          </div>

          <button 
            onClick={() => setActiveTab('financeiro')}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-semibold transition-all ${
              activeTab === 'financeiro' ? 'text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            }`}
            style={activeTab === 'financeiro' ? primaryBg : undefined}
          >
            <CreditCard size={16} />
            Financeiro
          </button>

          <button 
            onClick={() => setActiveTab('dados')}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-semibold transition-all ${
              activeTab === 'dados' ? 'text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            }`}
            style={activeTab === 'dados' ? primaryBg : undefined}
          >
            <Database size={16} />
            Dados Cadastrais
          </button>

          <button 
            onClick={() => setActiveTab('dominio')}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-semibold transition-all ${
              activeTab === 'dominio' ? 'text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            }`}
            style={activeTab === 'dominio' ? primaryBg : undefined}
          >
            <Globe size={16} />
            Domínio Próprio
          </button>

          <button 
            onClick={() => setActiveTab('suporte')}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-semibold transition-all ${
              activeTab === 'suporte' ? 'text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            }`}
            style={activeTab === 'suporte' ? primaryBg : undefined}
          >
            <LifeBuoy size={16} />
            Suporte
          </button>

          <button 
            onClick={() => setActiveTab('conta')}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-semibold transition-all ${
              activeTab === 'conta' ? 'text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            }`}
            style={activeTab === 'conta' ? primaryBg : undefined}
          >
            <Lock size={16} />
            Conta e Segurança
            {/*
              O ponto so aparece enquanto a senha for a de fabrica. E o unico
              lugar do painel que avisa isso sem interromper o que a pessoa
              estava fazendo.
            */}
            {precisaTrocarSenha && activeTab !== 'conta' && (
              <span className="ml-auto w-2 h-2 rounded-full bg-amber-500" title="Senha padrão — troque" />
            )}
          </button>
        </nav>

        <div className="p-3 border-t border-gray-100">
          <button 
            onClick={onLogout}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-bold text-red-600 hover:bg-red-50 transition-all"
          >
            <LogOut size={16} />
            Sair do Painel
          </button>
        </div>
      </div>

      {/* Main Content Area - Full Canvas Fluid SaaS */}
      <div className="flex-1 overflow-y-auto bg-slate-50/40 flex flex-col min-w-0">
        {renderTabContent()}
      </div>

      {/* Bottom Navigation - Mobile */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-t border-gray-200 shadow-[0_-4px_15px_rgba(0,0,0,0.03)] pb-safe">
        <div className="flex items-center justify-between px-1 py-1">
           <button onClick={() => setActiveTab('inicio')} className={`flex-1 flex flex-col items-center justify-center py-2 rounded-xl transition-all ${activeTab === 'inicio' ? 'font-bold' : 'text-gray-400'}`} style={activeTab === 'inicio' ? primaryText : undefined}>
             <Home size={18} />
             <span className="text-[10px] mt-1">Início</span>
           </button>
           <button onClick={() => setActiveTab('crm')} className={`flex-1 flex flex-col items-center justify-center py-2 rounded-xl transition-all ${activeTab === 'crm' ? 'font-bold' : 'text-gray-400'}`} style={activeTab === 'crm' ? primaryText : undefined}>
             <Kanban size={18} />
             <span className="text-[10px] mt-1">CRM</span>
           </button>
           <button onClick={() => setActiveTab('agendamento')} className={`flex-1 flex flex-col items-center justify-center py-2 rounded-xl transition-all ${activeTab === 'agendamento' ? 'font-bold' : 'text-gray-400'}`} style={activeTab === 'agendamento' ? primaryText : undefined}>
             <Clock size={18} />
             <span className="text-[10px] mt-1">Agenda</span>
           </button>
           <button onClick={() => setActiveTab('whatsapp')} className={`flex-1 flex flex-col items-center justify-center py-2 rounded-xl transition-all ${activeTab === 'whatsapp' ? 'font-bold' : 'text-gray-400'}`} style={activeTab === 'whatsapp' ? primaryText : undefined}>
             <Bot size={18} />
             <span className="text-[10px] mt-1">IA</span>
           </button>
           <button onClick={() => setActiveTab('identidade')} className={`flex-1 flex flex-col items-center justify-center py-2 rounded-xl transition-all ${activeTab === 'identidade' ? 'font-bold' : 'text-gray-400'}`} style={activeTab === 'identidade' ? primaryText : undefined}>
             <Palette size={18} />
             <span className="text-[10px] mt-1">Marca</span>
           </button>
        </div>
      </div>
    </div>
  );
};
